import { useEffect, useId, useMemo, useRef, useState, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";

import { DocumentRequestSubmitModal } from "@/components/DocumentRequestSubmitModal";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { Button } from "@/components/ui/Button";
import { DateInput, Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import {
  createDocumentRequest,
  DOCUMENT_REQUEST_PRIORITIES,
  documentRequestPriorityLabel,
  documentRequestStatusLabel,
  type DocumentRequestPriority,
  type DocumentRequestStatus,
} from "@/lib/documentRequest";
import {
  controlledDocumentIsDownloadable,
  documentKindWorkbenchFieldOptions,
  fetchDocumentLookups,
} from "@/lib/documents";
import { nextListSelection } from "@/lib/listModifierSelection";
import { hubSupportTicketPagePath } from "@/lib/navigationTrail";
import { cn } from "@/lib/utils";
import {
  rfqLineDocumentAccessPresentation,
  type RfqSkuDocumentAccessSummary,
} from "./rfqLineDocumentAccessPresentation";

export type RfqAttachableDocument = {
  id: string;
  title: string;
  original_filename: string;
  document_kind_label: string;
  revision: string;
  lifecycle_status: string;
  can_download?: boolean;
  can_read?: boolean;
  can_preview?: boolean;
  viewer_access_request?: { id: string; status: string } | null;
};

const SEARCH_STALE_MS = 15_000;

const ADD_FROM_DOCUMENTS_HOVER_HINT =
  "These are controlled documents for this part number in Document Control. The line part number only limits which files you can choose. Add the ones you want on this RFQ (supplier or internal, depending on which panel you opened). Files stay in Document Control; preview and email use the supplier attachments you added. Internal RFQ attachments are never emailed.\n\nSelect one or more documents. Use checkboxes, Control-click or Command-click to toggle, and Shift-click for a range.";

const ADD_FROM_DOCUMENTS_HEADER_HOVER_HINT =
  "Choose a Document Control file you can already download, including files shared with you.";

function documentMatchesSearch(document: RfqAttachableDocument, query: string): boolean {
  if (query === "") return true;
  const haystack = `${document.title} ${document.original_filename} ${document.document_kind_label}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function filterSkuDocuments(
  skuDocuments: RfqAttachableDocument[],
  query: string,
): RfqAttachableDocument[] {
  return skuDocuments.filter((document) => documentMatchesSearch(document, query));
}

export function useRfqSkuDocumentAccessPresentation(sku: string): {
  hasAccess: boolean;
  helperText: string;
  buttonLabel: string;
} {
  const lineSku = sku.trim();
  const documentsQuery = useQuery<
    {
      documents: RfqAttachableDocument[];
      access?: RfqSkuDocumentAccessSummary;
    },
    ApiError
  >({
    queryKey: ["documents", "sku-detail", lineSku],
    queryFn: () =>
      apiFetch<{
        documents: RfqAttachableDocument[];
        access?: RfqSkuDocumentAccessSummary;
      }>("/api/document-accessible-for-sku", { query: { sku: lineSku } }),
    enabled: lineSku !== "",
    staleTime: SEARCH_STALE_MS,
    refetchOnWindowFocus: false,
  });

  return useMemo(
    () =>
      rfqLineDocumentAccessPresentation(
        documentsQuery.data?.documents ?? [],
        documentsQuery.data?.access,
      ),
    [documentsQuery.data?.access, documentsQuery.data?.documents],
  );
}

function viewerAccessRequestStatusLabel(status: string): string {
  return documentRequestStatusLabel(status as DocumentRequestStatus);
}

export function RfqLineDocumentAccessRow(props: {
  sku: string;
  stagedDocumentCount?: number;
  isSubmitting?: boolean;
  onAttachDocuments?: (documentRecordIds: string[]) => void | Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const presentation = useRfqSkuDocumentAccessPresentation(props.sku);
  const canAttachLine = props.onAttachDocuments != null;
  const stagedLabel =
    props.stagedDocumentCount != null && props.stagedDocumentCount > 0
      ? `${props.stagedDocumentCount} document${props.stagedDocumentCount === 1 ? "" : "s"} staged`
      : null;
  const helperText =
    stagedLabel != null
      ? `${stagedLabel} for this line (attached when you save the RFQ).`
      : presentation.helperText;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 bg-white px-3 py-2">
      <p className="text-xs text-slate-500">{helperText}</p>
      <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
        {canAttachLine ? "Add from documents" : presentation.buttonLabel}
      </Button>
      <RfqAttachDocumentModal
        open={open}
        sku={props.sku}
        canAttach={canAttachLine}
        viewerHasAccess={presentation.hasAccess}
        isSubmitting={props.isSubmitting}
        onClose={() => setOpen(false)}
        onAttach={async (documentRecordIds) => {
          await props.onAttachDocuments?.(documentRecordIds);
          setOpen(false);
        }}
      />
    </div>
  );
}

function RfqAttachInlineNewDocumentRequest(props: {
  sku: string;
  disabled?: boolean;
  onSubmitted: () => void;
  onCancel: () => void;
}): JSX.Element {
  const formId = useId();
  const { showToast } = useHubToast();
  const [body, setBody] = useState("");
  const [documentKindCode, setDocumentKindCode] = useState("");
  const [documentKindSearch, setDocumentKindSearch] = useState("");
  const [priority, setPriority] = useState<DocumentRequestPriority | "">("");
  const [requestedByDate, setRequestedByDate] = useState("");

  const lookupsQuery = useQuery({
    queryKey: ["document-lookups"],
    queryFn: fetchDocumentLookups,
  });

  const documentKindOptions = useMemo(
    () => documentKindWorkbenchFieldOptions(lookupsQuery.data?.kinds ?? []),
    [lookupsQuery.data?.kinds],
  );

  const submitMutation = useMutation({
    mutationFn: () => {
      if (body.trim() === "") {
        throw new Error("Please describe the document you need.");
      }
      if (priority === "") {
        throw new Error("Please select a priority.");
      }
      return createDocumentRequest({
        kind: "new_document",
        sku: props.sku,
        documentKindCode: documentKindCode.trim() !== "" ? documentKindCode.trim() : null,
        priority,
        requestedByDate: requestedByDate.trim() !== "" ? requestedByDate.trim() : null,
        body: body.trim(),
        pagePath: hubSupportTicketPagePath(window.location.pathname, window.location.search),
      });
    },
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message: `Document request #${result.id} was submitted.`,
      });
      props.onSubmitted();
    },
    onError: (error: Error) => showToast({ tone: "error", message: error.message }),
  });

  return (
    <form
      id={formId}
      className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submitMutation.mutateAsync();
      }}
    >
      <p className="text-sm font-medium text-slate-900">Request a missing document</p>
      <p className="mt-1 text-xs text-slate-600">
        Describe what controlled document you need for this part number. Document Control will respond in the request
        queue.
      </p>
      <div className="mt-3 space-y-3">
        <div>
          <Label htmlFor={`${formId}-document-kind`}>Document type (optional)</Label>
          <SearchableSelect
            id={`${formId}-document-kind`}
            className="mt-1"
            value={documentKindCode}
            onValueChange={(option) => {
              setDocumentKindCode(option?.value ?? "");
              setDocumentKindSearch("");
            }}
            options={documentKindOptions}
            searchValue={documentKindSearch}
            onSearchChange={setDocumentKindSearch}
            placeholder="Not set"
            searchPlaceholder="Search document types"
            emptyMessage="No matching document types."
            isLoading={lookupsQuery.isLoading}
          />
        </div>
        <div>
          <Label htmlFor={`${formId}-priority`}>Priority</Label>
          <select
            id={`${formId}-priority`}
            required
            value={priority}
            onChange={(event) => setPriority(event.target.value as DocumentRequestPriority | "")}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="" disabled>
              Select priority…
            </option>
            {DOCUMENT_REQUEST_PRIORITIES.map((code) => (
              <option key={code} value={code}>
                {documentRequestPriorityLabel(code)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={`${formId}-requested-by-date`}>Request by date (optional)</Label>
          <DateInput
            id={`${formId}-requested-by-date`}
            value={requestedByDate}
            onChange={(event) => setRequestedByDate(event.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor={`${formId}-body`}>What document do you need?</Label>
          <textarea
            id={`${formId}-body`}
            required
            rows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            disabled={props.disabled || submitMutation.isPending}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Which drawing, spec, or package is missing and why you need it…"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={props.onCancel} disabled={submitMutation.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={props.disabled || submitMutation.isPending} isLoading={submitMutation.isPending}>
          Submit request
        </Button>
      </div>
    </form>
  );
}

export function RfqAttachDocumentModal(props: {
  open: boolean;
  sku?: string | null;
  canAttach?: boolean;
  viewerHasAccess?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onAttach: (documentRecordIds: string[]) => void | Promise<void>;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const anchorIdRef = useRef<string | null>(null);
  const [accessDocument, setAccessDocument] = useState<{ id: string; title: string } | null>(null);
  const [newDocumentPanelOpen, setNewDocumentPanelOpen] = useState(false);
  const lineSku = props.sku?.trim() ?? "";
  const canAttach = props.canAttach !== false;
  const viewerHasAccess = props.viewerHasAccess === true;
  const trimmedSearch = debouncedSearch.trim();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    if (!props.open) {
      setSearch("");
      setDebouncedSearch("");
      setSelectedIds([]);
      anchorIdRef.current = null;
      setNewDocumentPanelOpen(false);
      setAccessDocument(null);
    }
  }, [props.open]);

  const invalidateSkuDocuments = () => {
    if (lineSku === "") return;
    void queryClient.invalidateQueries({ queryKey: ["documents", "sku-detail", lineSku] });
  };

  const skuDocumentsQuery = useQuery<{ documents: RfqAttachableDocument[] }, ApiError>({
    queryKey: ["documents", "sku-detail", lineSku],
    queryFn: () =>
      apiFetch<{ documents: RfqAttachableDocument[] }>("/api/document-accessible-for-sku", {
        query: { sku: lineSku },
      }),
    enabled: props.open && lineSku !== "",
    staleTime: SEARCH_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const searchableDocumentsQuery = useQuery<{ documents: RfqAttachableDocument[] }, ApiError>({
    queryKey: ["document-attachable", trimmedSearch, "header"],
    queryFn: () =>
      apiFetch<{ documents: RfqAttachableDocument[] }>("/api/document-attachable", {
        query: { q: trimmedSearch || undefined },
      }),
    enabled: props.open && lineSku === "" && trimmedSearch !== "",
    staleTime: SEARCH_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const headerSearchDocumentsQuery = useQuery<{ documents: RfqAttachableDocument[] }, ApiError>({
    queryKey: ["document-attachable", "header-empty"],
    queryFn: () => apiFetch<{ documents: RfqAttachableDocument[] }>("/api/document-attachable", {}),
    enabled: props.open && lineSku === "" && trimmedSearch === "",
    staleTime: SEARCH_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const documents = useMemo(() => {
    if (lineSku !== "") {
      return filterSkuDocuments(skuDocumentsQuery.data?.documents ?? [], trimmedSearch);
    }
    if (trimmedSearch !== "") {
      return searchableDocumentsQuery.data?.documents ?? [];
    }
    return headerSearchDocumentsQuery.data?.documents ?? [];
  }, [
    lineSku,
    headerSearchDocumentsQuery.data?.documents,
    searchableDocumentsQuery.data?.documents,
    skuDocumentsQuery.data?.documents,
    trimmedSearch,
  ]);

  const attachableDocuments = useMemo(
    () => documents.filter((document) => controlledDocumentIsDownloadable(document)),
    [documents],
  );
  const visibleAttachableIds = useMemo(
    () => attachableDocuments.map((document) => document.id),
    [attachableDocuments],
  );
  const selectedAttachableIds = useMemo(
    () => selectedIds.filter((id) => visibleAttachableIds.includes(id)),
    [selectedIds, visibleAttachableIds],
  );

  const applySelection = (clickedId: string, modifiers: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => {
    const next = nextListSelection({
      visibleIds: visibleAttachableIds,
      selectedIds: selectedAttachableIds,
      clickedId,
      anchorId: anchorIdRef.current,
      modifiers,
    });
    anchorIdRef.current = next.anchorId;
    setSelectedIds(next.selectedIds);
  };

  const toggleCheckbox = (documentId: string) => {
    applySelection(documentId, { shiftKey: false, ctrlKey: true, metaKey: false });
  };

  const handleRowClick = (event: MouseEvent<HTMLButtonElement>, documentId: string) => {
    applySelection(documentId, {
      shiftKey: event.shiftKey,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
    });
  };

  if (!props.open) {
    return null;
  }

  const listError =
    lineSku !== ""
      ? skuDocumentsQuery.error
      : trimmedSearch !== ""
        ? searchableDocumentsQuery.error
        : headerSearchDocumentsQuery.error;
  const listLoading =
    lineSku !== ""
      ? skuDocumentsQuery.isLoading
      : trimmedSearch !== ""
        ? searchableDocumentsQuery.isLoading
        : headerSearchDocumentsQuery.isLoading;
  const addButtonLabel =
    selectedAttachableIds.length <= 1
      ? "Add document"
      : `Add ${selectedAttachableIds.length} documents`;

  const hoverHint = lineSku !== "" ? ADD_FROM_DOCUMENTS_HOVER_HINT : ADD_FROM_DOCUMENTS_HEADER_HOVER_HINT;

  return (
    <>
      <DocumentModal
        title={
          canAttach
            ? "Add from documents"
            : viewerHasAccess
              ? "Documents for this part number"
              : "Request document access"
        }
        titleTrailing={canAttach ? <HoverHintWithGlyph hoverHint={hoverHint} size="md" /> : undefined}
        onClose={props.onClose}
        showCloseButton={false}
        shellClassName="flex max-h-[min(720px,calc(100dvh-2rem))] min-h-[520px] max-w-lg flex-col"
        overlayClassName="z-[55] px-4 py-8"
        mainClassName="flex min-h-0 flex-1 flex-col"
      >
        <div className="mt-1 flex min-h-0 flex-1 flex-col">
          {!canAttach ? (
            <p className="mb-3 text-sm text-slate-600">
              {lineSku !== ""
                ? viewerHasAccess
                  ? "These files are listed in Document Control for this part number. Preview and download depend on your role and any shares. Locked files can still be requested."
                  : "You can see which files exist for this part number. Attaching a copy is a download: it needs Document Control administrator access or a share. Locked files can be requested."
                : "Choose a Document Control file you can already download, including files shared with you."}
            </p>
          ) : null}
          <div>
            <Label htmlFor="rfq-attach-document-search">Search documents</Label>
            <Input
              id="rfq-attach-document-search"
              type="search"
              className="mt-1"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedIds([]);
                anchorIdRef.current = null;
              }}
              placeholder={
                lineSku !== ""
                  ? "Filter by title, file name, or document type"
                  : "Filter by title, file name, or part number"
              }
            />
          </div>
          <div className="mt-3 min-h-0 flex-1 overflow-auto rounded-md border border-slate-200">
            {listLoading ? (
              <p className="px-3 py-6 text-sm text-slate-500">Loading documents…</p>
            ) : listError ? (
              <p className="px-3 py-6 text-sm text-rose-700">{listError.message}</p>
            ) : documents.length === 0 ? (
              <p className="px-3 py-6 text-sm text-slate-500">
                {trimmedSearch !== ""
                  ? "No documents match these filters."
                  : lineSku !== ""
                    ? "No controlled documents are targeted at this part number."
                    : "No documents you can attach were found."}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {documents.map((document) => {
                  const canDownload = controlledDocumentIsDownloadable(document);
                  const isSelected = canAttach && canDownload && selectedAttachableIds.includes(document.id);
                  const accessRequest = document.viewer_access_request;
                  return (
                    <li key={document.id} className="flex items-start gap-2 px-2 py-1">
                      {canAttach && canDownload ? (
                        <input
                          type="checkbox"
                          className="mt-2.5 h-4 w-4 shrink-0 rounded border-slate-300"
                          checked={isSelected}
                          aria-label={`Select ${document.title}`}
                          onChange={() => toggleCheckbox(document.id)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      ) : (
                        <span className="mt-2.5 w-4 shrink-0" aria-hidden />
                      )}
                      <button
                        type="button"
                        disabled={!canAttach || !canDownload}
                        className={cn(
                          "min-w-0 flex-1 rounded-md px-2 py-1.5 text-left text-sm",
                          canDownload && canAttach ? "hover:bg-slate-50" : "cursor-default bg-slate-50/60",
                          isSelected && "bg-brand-50 hover:bg-brand-50",
                        )}
                        onClick={(event) => {
                          if (!canAttach || !canDownload) return;
                          handleRowClick(event, document.id);
                        }}
                      >
                        <p className="flex items-center gap-2 font-medium text-slate-900">
                          {!canDownload ? <Lock className="h-4 w-4 shrink-0 text-slate-400" aria-hidden /> : null}
                          {document.title}
                        </p>
                        <p className="text-xs text-slate-500">
                          {document.document_kind_label}
                          {document.revision ? ` · Rev ${document.revision}` : ""}
                          {` · ${document.original_filename}`}
                        </p>
                      </button>
                      {!canDownload ? (
                        accessRequest != null ? (
                          <div className="mt-1 max-w-[7.5rem] shrink-0 text-right text-xs text-slate-600">
                            <p className="font-medium text-slate-800">
                              {viewerAccessRequestStatusLabel(accessRequest.status)}
                            </p>
                            <p className="text-slate-500">Request #{accessRequest.id}</p>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="mt-1 shrink-0"
                            onClick={() => setAccessDocument({ id: document.id, title: document.title })}
                          >
                            Request access
                          </Button>
                        )
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {lineSku !== "" && newDocumentPanelOpen ? (
            <RfqAttachInlineNewDocumentRequest
              sku={lineSku}
              disabled={props.isSubmitting}
              onCancel={() => setNewDocumentPanelOpen(false)}
              onSubmitted={() => {
                setNewDocumentPanelOpen(false);
                invalidateSkuDocuments();
              }}
            />
          ) : null}
          <div className="mt-4 flex shrink-0 flex-wrap items-center justify-end gap-2">
            {lineSku !== "" ? (
              <Button
                type="button"
                variant="secondary"
                className="mr-auto"
                disabled={props.isSubmitting || newDocumentPanelOpen}
                onClick={() => setNewDocumentPanelOpen(true)}
              >
                Request a document
              </Button>
            ) : null}
            <Button type="button" variant="secondary" onClick={props.onClose} disabled={props.isSubmitting}>
              Cancel
            </Button>
            {canAttach ? (
              <Button
                type="button"
                disabled={selectedAttachableIds.length === 0 || props.isSubmitting}
                isLoading={props.isSubmitting}
                onClick={() => {
                  if (selectedAttachableIds.length === 0) return;
                  void props.onAttach(selectedAttachableIds);
                }}
              >
                {addButtonLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </DocumentModal>
      {lineSku !== "" && accessDocument != null ? (
        <DocumentRequestSubmitModal
          open
          kind="access"
          sku={lineSku}
          document={accessDocument}
          onClose={() => setAccessDocument(null)}
          onSubmitted={() => {
            setAccessDocument(null);
            invalidateSkuDocuments();
          }}
        />
      ) : null}
    </>
  );
}
